import _ from "lodash";
import React, { PureComponent } from "react";
import { getCurrentPathname } from "services/NavigationService";
import classes from "./EditOrgInfo.scss";
import withLoader from "UIComponents/GqlWithLoader";
import ProgressSteps from "UIComponents/ProgressSteps";
import I18nHOC from "UIComponents/I18nHOC";
import { connect } from "react-redux";
import { graphql, compose } from "lib/apolloClient";
import Step1 from "./components/Step1";
import Step2 from "./components/Step2";
import Step3 from "./components/Step3";
import EditBasicDetails from "./components/EditBasicDetails";
import PropTypes from "prop-types";
import { colors, GRAPHQL_ONGOING_QUERY_NETWORK_STATUS } from "Constants";
import { getOrganizationGradesQuery } from "GradesSetup/modules/Queries";
import {
  getOrganizationDetailsQuery,
  getOrganizationAttachmentDetailsQuery,
} from "Tooling/modules/ToolingQueries";
import {
  getOrganizationDetailsFromCache,
  getOrganizationFeedDetailsFromCache,
  getOrganizationAttachmentDetailsFromCache,
} from "Tooling/modules/ToolingGraphqlHelpers";
import { getOrganizationFeedDetailsFromCacheLocal } from "./modules/UpsertOrganizationHelpers";
import classNames from "classnames";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import {
  checkAcademicYears,
  checkDPAcademicYears,
  transformAcademicYears,
} from "Tooling/modules/ToolingModule";
import {
  getOrganizationDetailsByMode,
  getOrganizationDetails,
  getModifiedSchoolsPlanDetails,
} from "Tooling/modules/ToolingUtils";
import { Button, DSProvider } from "@toddle-edu/ds-web";
import { generateRandomId } from "Utils";

import update from "immutability-helper";
import { CURRICULUM_TYPE_DP, SCHOOL } from "Constants/stringConstants";
import { getGlobalGradesGroupedByCurriculumProgram } from "./modules/UpsertOrganizationUtils";
import AddOnManager from "InternalTools/components/AddOnManager/AddOnManager";
import { getIsAddOnManagerV2Enabled } from "Utils/ACLStoreUtils";

const steps = [1, 2, 3];
const stepsWithAddOnManager = [1, 2, 3, 4];
class EditOrgInfo extends PureComponent {
  constructor(props) {
    super(props);
    this.step1Ref = React.createRef();
    this.step2Ref = React.createRef();
    this.step3Ref = React.createRef();
    this.step4Ref = React.createRef();
    this.editBasicDetailsRef = React.createRef();

    this.scrollContainerRef = React.createRef();

    this.state = {
      currentStep: 1,
      organizationDetails: {},
      modifiedSchoolPlanDetails: [],
    };
  }

  initializeStateForCreateMode = () => {
    const { organizationDetails } = this.props;

    this.setState({
      organizationDetails: {
        ...organizationDetails,
        curriculumPrograms: [
          {
            id: generateRandomId(),
            isNewCurriculumProgram: true,
          },
        ],
      },
    });
  };

  initializeStateForEditCurriculumProgramsMode = () => {
    const {
      globalGradesGroupedByCurriculumProgram,
      organizationDetails,
    } = this.props;

    const academicYears = transformAcademicYears({
      academicYears: _.get(organizationDetails, "academicYears", []),
    });

    const curriculumPrograms = _.map(
      _.get(organizationDetails, "curriculumOptions", []),
      ({
        curriculumType,
        planId,
        rosteringMethod,
        toddleBuddyId,
        id,
        label,
        acronym,
        programDataLanguages,
        variant,
        dealId,
        platformId,
        studentCount,
        curriculumProgramData,
        examSessionMonth,
      }) => {
        const selectedGlobalGrades = globalGradesGroupedByCurriculumProgram[id];
        const gradesToBeIncluded = _.map(selectedGlobalGrades, ({ id }) => id);
        const isDpCurriculumProgram = curriculumType === CURRICULUM_TYPE_DP;
        const examSessionMonthSelected = !_.isNil(examSessionMonth)
          ? examSessionMonth
          : _.get(organizationDetails, "examSessionMonth", null);
        return {
          ...(isDpCurriculumProgram
            ? {
                examSessionMonth: examSessionMonthSelected,
                orgDpExamSession: examSessionMonthSelected,
              }
            : {}),
          grades: gradesToBeIncluded,
          name: label,
          acronym,
          rosteringMethod,
          toddleBuddy: toddleBuddyId,
          variant,
          type: curriculumType,
          plan: planId,
          id,
          programDataLanguages,
          platformId,
          dealId,
          studentCount,
          curriculumProgramData,
        };
      }
    );

    const updatedOrganizationDetails = {
      id: organizationDetails.id,
      academicYears,
      curriculumPrograms: curriculumPrograms,
    };

    this.setState({
      organizationDetails: updatedOrganizationDetails,
      currentStep: 2,
    });
  };

  initializeStateForEditBasicDetailsMode = () => {
    const { organizationDetails } = this.props;

    this.setState({
      organizationDetails,
      currentStep: "editBasicDetails",
    });
  };

  initializeStateForAddOnManagerMode = () => {
    const { organizationDetails } = this.props;

    this.setState({
      organizationDetails,
      currentStep: 4,
    });
  };

  setOrganizationDetails = organizationDetails => {
    this.setState({
      organizationDetails,
    });
  };

  componentDidMount = () => {
    const { mode } = this.props;
    switch (mode) {
      case "create": {
        this.initializeStateForCreateMode();
        break;
      }

      case "editCurriculumPrograms": {
        this.initializeStateForEditCurriculumProgramsMode();
        break;
      }

      case "editAddOns": {
        this.initializeStateForAddOnManagerMode();
        break;
      }

      case "editBasicDetails": {
        this.initializeStateForEditBasicDetailsMode();
      }
    }
  };

  componentDidUpdate = (prevProps, prevState) => {
    const { isLoading: oldLoadingStatus } = prevProps;
    const { isData, isLoading, mode } = this.props;

    const { currentStep: oldCurrentStep } = prevState;
    const { currentStep } = this.state;

    if (
      oldLoadingStatus !== isLoading &&
      isData &&
      mode == "editCurriculumPrograms"
    ) {
      this.initializeStateForEditCurriculumProgramsMode();
    }

    if (
      oldCurrentStep !== currentStep &&
      currentStep === 2 &&
      mode === "create"
    ) {
      this.addProgramDataLanguage();
    }
  };

  scrollToTop = () => {
    this.scrollContainerRef.current.scrollTop = 0;
  };

  addProgramDataLanguage = () => {
    const { organizationDetails } = this.state;
    const { curriculumPrograms } = organizationDetails;

    const updatedCurriculumPrograms = _.map(
      curriculumPrograms,
      curriculumProgram => {
        return {
          ...curriculumProgram,
          programDataLanguages: [_.get(organizationDetails, "locale", "en")],
        };
      }
    );

    const updatedOrganizationDetails = {
      ...organizationDetails,
      curriculumPrograms: updatedCurriculumPrograms,
    };

    this.setState({
      organizationDetails: updatedOrganizationDetails,
    });
  };

  checkDpAcademicYears = () => {
    const { organizationDetails } = this.state;

    const { academicYears } = organizationDetails;

    const transformedAcademicYears = _.map(
      academicYears,
      ({ dateDurationValue }) => dateDurationValue
    );

    //Checking if DP school created has 1prev, current and 1 next academic year
    return checkDPAcademicYears({
      academicYears: transformedAcademicYears,
    });
  };

  validateCurrentStep = () => {
    const { currentStep, organizationDetails } = this.state;
    const { setToastMsg, mode } = this.props;
    switch (currentStep) {
      case 1: {
        return this.step1Ref?.current?.isValid();
      }

      case 3: {
        const { academicYears, curriculumPrograms } = organizationDetails;

        const isValidTimeZone = this.step2Ref?.current?.isValid();

        const dpCurriculumPrograms = _.filter(
          curriculumPrograms,
          ({ type }) => type == CURRICULUM_TYPE_DP
        );

        if (!_.isEmpty(dpCurriculumPrograms)) {
          //Checking if DP school created has 1prev, current and 1 next academic year
          const academicYearsValid = this.checkDpAcademicYears();
          if (!academicYearsValid) {
            setToastMsg("toastMsgs:please_select_one_prev_current_next_year");
            return false;
          }
        }

        const areAcademicYearsValid = checkAcademicYears({ academicYears });
        if (!areAcademicYearsValid) {
          setToastMsg(
            "toastMsgs:multiple_academic_years_cannot_have_same_dates"
          );
          return false;
        }

        return isValidTimeZone;
      }

      case 2: {
        const totalErrors = this.step3Ref?.current?.isValid();

        const { curriculumPrograms } = organizationDetails;
        const dpCurriculumPrograms = _.filter(
          curriculumPrograms,
          ({ type }) => type == CURRICULUM_TYPE_DP
        );

        const areDpCurriculumProgramsEmpty = _.isEmpty(dpCurriculumPrograms);

        const areCurriculumProgramsEmpty = _.isEmpty(curriculumPrograms);

        let areAcademicYearsValid = true;

        const areDpCurriculumProgramsValid = _.reduce(
          dpCurriculumPrograms,
          (result, { examSessionMonth }) => {
            return result && !!examSessionMonth;
          },
          true
        );

        if (areCurriculumProgramsEmpty) {
          setToastMsg("toastMsgs:school_must_have_one_curriculum");
        } else if (!areDpCurriculumProgramsValid) {
          setToastMsg("toastMsgs:please_select_exam_session_month");
        } else if (
          !areDpCurriculumProgramsEmpty &&
          mode === "editCurriculumPrograms"
        ) {
          areAcademicYearsValid = this.checkDpAcademicYears();

          if (!areAcademicYearsValid) {
            setToastMsg("toastMsgs:please_create_one_prev_current_next_year");
          }
        }

        return (
          totalErrors === 0 &&
          areDpCurriculumProgramsValid &&
          areAcademicYearsValid &&
          !areCurriculumProgramsEmpty
        );
      }

      case 4: {
        const isValid = this.step4Ref?.current?.isValid();
        if (!isValid) setToastMsg("toastMsgs:configure_addons_error_msg");
        return isValid;
      }

      case "editBasicDetails": {
        return this.editBasicDetailsRef?.current?.isValid();
      }
    }
  };
  onClickNext = async () => {
    const { currentStep, organizationDetails } = this.state;
    const { onClickOperation, closeModal } = this.props;
    const isValid = this.validateCurrentStep();
    if (!isValid) {
      return;
    }

    if (currentStep === 3) {
      const res = await onClickOperation({ ...organizationDetails });
      if (!getIsAddOnManagerV2Enabled()) return;

      if (_.get(res, "isSuccess", false)) {
        const organization = _.get(res, "organization", {});
        const curriculumOptions = _.get(organization, "curriculumPrograms", []);
        const updatedOrgDetails = _.omit(organization, ["curriculumPrograms"]);

        this.setState(prevState => ({
          ...prevState,
          organizationDetails: { ...updatedOrgDetails, curriculumOptions },
          currentStep: prevState.currentStep + 1,
        }));
        return;
      }
      return;
    }

    if (currentStep === 4) {
      await this.step4Ref?.current?.onSave();
      return;
    }

    this.setState(prevState => ({
      ...prevState,
      currentStep: prevState.currentStep + 1,
    }));

    this.scrollToTop();
  };

  onClickCancel = () => {
    this.setState(prevState => ({
      ...prevState,
      currentStep: prevState.currentStep - 1,
    }));
  };

  updateOrganizationInformation = ({ key, value }) => {
    this.setState(prevState => ({
      ...prevState,
      organizationDetails: { ...prevState.organizationDetails, [key]: value },
    }));
  };

  addEntity = ({ entity }) => {
    const { mode, organizationDetails } = this.props;
    const { organizationDetails: stateOrganizationDetails } = this.state;
    let field;

    const entityInfo = { id: generateRandomId() };
    const programDataLanguages =
      mode === "create"
        ? [stateOrganizationDetails.locale]
        : [organizationDetails.locale];

    switch (entity) {
      case "CURRICULUM": {
        field = "curriculumPrograms";
        entityInfo.isNewCurriculumProgram = true;
        entityInfo.programDataLanguages = programDataLanguages;
      }
    }

    this.setState(prevState => {
      return update(prevState, {
        organizationDetails: {
          [field]: {
            $set: [entityInfo, ...(prevState.organizationDetails[field] ?? [])],
          },
        },
      });
    });
  };

  updateEntity = ({ entity, entityDetails }) => {
    let field;
    switch (entity) {
      case "CURRICULUM": {
        field = "curriculumPrograms";
      }
    }

    this.setState(prevState => {
      return update(prevState, {
        organizationDetails: {
          [field]: {
            $set: _.map(
              prevState.organizationDetails[field],
              oldEntityDetails => {
                if (oldEntityDetails.id == entityDetails.id) {
                  return { ...oldEntityDetails, ...entityDetails };
                }
                return oldEntityDetails;
              }
            ),
          },
        },
      });
    });
  };

  deleteEntity = ({ entity, entityId }) => {
    let field;
    switch (entity) {
      case "CURRICULUM": {
        field = "curriculumPrograms";
      }
    }

    this.setState(prevState => {
      const entityDetails = prevState.organizationDetails[field];

      const updatedEntityDetails = _.filter(
        entityDetails,
        ({ id }) => id !== entityId
      );

      return update(prevState, {
        organizationDetails: {
          [field]: {
            $set: updatedEntityDetails,
          },
        },
      });
    });
  };

  getCurrentStepClasses = () => {
    const { currentStep } = this.state;
    switch (currentStep) {
      case 1: {
        return classNames(classes.stepContainer, classes.step1Container);
      }
      case 3: {
        return classNames(classes.stepContainer, classes.step3Container);
      }
      case 2: {
        return classNames(classes.stepContainer, classes.step2Container);
      }
      case 4: {
        return classNames(classes.stepContainer, classes.step4Container);
      }
      case "editBasicDetails": {
        return classNames(
          classes.stepContainer,
          classes.editBasicDetailsContainer
        );
      }
    }
  };

  getCurrentStepElement = () => {
    const { organizationDetails, currentStep } = this.state;
    const { isAccessManagerV2 = false, closeModal } = this.props;

    const {
      id,
      name,
      code,
      locale,
      rosteringMethod,
      attachments,
      academicYears,
      operationalDays,
      curriculumPrograms,
      timezone,
      storageType,
    } = organizationDetails;

    const {
      toddleBuddies,
      mode,
      globalGradesGroupedByCurriculumProgram,
    } = this.props;
    switch (currentStep) {
      case 1: {
        return (
          <Step1
            customRef={this.step1Ref}
            name={name}
            code={code}
            locale={locale}
            rosteringMethod={rosteringMethod}
            attachments={attachments}
            storageType={storageType}
            updateOrganizationInformation={this.updateOrganizationInformation}
            isAccessManagerV2={isAccessManagerV2}
          />
        );
      }
      case 3: {
        return (
          <Step2
            academicYears={academicYears}
            timezone={timezone}
            operationalDays={operationalDays}
            updateOrganizationInformation={this.updateOrganizationInformation}
            customRef={this.step2Ref}
          />
        );
      }
      case 2: {
        const addCurriculumProgram = () =>
          this.addEntity({ entity: "CURRICULUM" });

        const updateCurriculumProgram = curriculumProgramDetails =>
          this.updateEntity({
            entity: "CURRICULUM",
            entityDetails: curriculumProgramDetails,
          });

        const deleteCurriculumProgram = ({ id }) =>
          this.deleteEntity({ entity: "CURRICULUM", entityId: id });

        return (
          <Step3
            customRef={this.step3Ref}
            addCurriculumProgram={addCurriculumProgram}
            curriculumPrograms={curriculumPrograms}
            updateCurriculumProgram={updateCurriculumProgram}
            toddleBuddies={toddleBuddies}
            mode={mode}
            deleteCurriculumProgram={deleteCurriculumProgram}
            globalGradesGroupedByCurriculumProgram={
              globalGradesGroupedByCurriculumProgram
            }
            organizationId={id}
            setOrganizationDetails={this.setOrganizationDetails}
            organizationDetails={organizationDetails}
          />
        );
      }
      case "editBasicDetails": {
        return (
          <EditBasicDetails
            customRef={this.editBasicDetailsRef}
            name={name}
            code={code}
            attachments={attachments}
            updateOrganizationInformation={this.updateOrganizationInformation}
          />
        );
      }
      case 4: {
        return (
          <DSProvider dsVersion="2.0">
            <AddOnManager
              customRef={this.step4Ref}
              organizationDetails={organizationDetails}
              isCreateFlow={_.isEqual(mode, "create")}
              isToddle20Flow={false}
              closeModal={closeModal}
              modifiedSchoolPlanDetails={this.state.modifiedSchoolPlanDetails}
            />
          </DSProvider>
        );
      }
    }
  };

  getNextButtonLabel = () => {
    const { t } = this.props;
    const { currentStep } = this.state;

    if (currentStep === 3) return t("common:create");
    if (currentStep === 4) return t("common:save");
    return t("common:next");
  };

  renderCreateOrganizationFlow = () => {
    const { currentStep } = this.state;
    const { t, closeModal } = this.props;

    const currentStepClasses = this.getCurrentStepClasses();
    const isAddOnManagerV2Enabled = getIsAddOnManagerV2Enabled();
    const canMoveBackToPreviousStep = _.includes([2, 3], currentStep);

    return (
      <div className={classes.container}>
        <ProgressSteps
          steps={isAddOnManagerV2Enabled ? stepsWithAddOnManager : steps}
          currentStep={currentStep}
          minHeight={8}
          currentStepBackgroundColor={colors.violet500}
        />

        <div className={classes.scrollContainer} ref={this.scrollContainerRef}>
          <div className={currentStepClasses}>
            {this.getCurrentStepElement()}
          </div>
        </div>
        <div className={classes.footer}>
          <div className={classes.footerContent}>
            <Button
              variant={"plain"}
              size={"large"}
              onClick={
                canMoveBackToPreviousStep ? this.onClickCancel : closeModal
              }
            >
              {canMoveBackToPreviousStep
                ? t("common:back")
                : t("common:cancel")}
            </Button>
            <Button
              variant={"primary"}
              size={"large"}
              className={classes.nextButton}
              onClick={this.onClickNext}
            >
              {this.getNextButtonLabel()}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  handleSuccessfulOperation = ({
    prevOrganizationDetails,
    currentOrganizationDetails,
  }) => {
    const { closeModal } = this.props;

    const prevPorgramDetails = prevOrganizationDetails?.curriculumOptions;

    const currentProgramDetails = _.filter(
      currentOrganizationDetails?.curriculumPrograms,
      ({ programType }) => programType === SCHOOL
    );

    const programPlanChanges = getModifiedSchoolsPlanDetails({
      prevSchoolDetails: prevPorgramDetails,
      currentSchoolsDetails: currentProgramDetails,
    });

    const isAddOnManagerV2Enabled = getIsAddOnManagerV2Enabled();

    if (_.isEmpty(programPlanChanges) || !isAddOnManagerV2Enabled) {
      closeModal();
      return;
    }

    // If there were change in plans(edit mode) then take the user to add-on manager to show the impact of plan changes on add-ons

    this.setState(prevState => ({
      ...prevState,
      modifiedSchoolPlanDetails: programPlanChanges,
      currentStep: 4,
      organizationDetails: {
        ...prevState.organizationDetails,
        curriculumOptions: currentProgramDetails,
      },
    }));
  };

  updateOrganizationDetailsHandler = async () => {
    const {
      organizationDetails: organizationDetailsFromState,
      currentStep,
    } = this.state;
    const {
      onClickOperation,
      mode,
      organizationDetails: organizationDetailsFromProps,
      closeModal,
    } = this.props;
    const isValid = this.validateCurrentStep();
    let updatedOrgDetails = organizationDetailsFromState;
    if (!isValid) {
      return;
    }

    if (mode === "editBasicDetails") {
      const { attachments: oldAttachments = [] } = organizationDetailsFromProps;
      const { attachments: newAttachments = [] } = updatedOrgDetails;
      const addedAttachments = _.differenceWith(
        newAttachments,
        oldAttachments,
        _.isEqual
      );
      const removedAttachments = _.map(
        _.differenceWith(oldAttachments, newAttachments, _.isEqual),
        ({ id }) => id
      );
      updatedOrgDetails = {
        ...updatedOrgDetails,
        addedAttachments,
        removedAttachments,
      };
    }

    if (currentStep === 4) {
      await this.step4Ref?.current?.onSave();
      return;
    }

    try {
      const result = await onClickOperation({ ...updatedOrgDetails });

      if (mode === "editCurriculumPrograms") {
        const { isSuccess, updatedOrganizationDetails } = result;

        if (!isSuccess) return;

        this.handleSuccessfulOperation({
          prevOrganizationDetails: organizationDetailsFromProps,
          currentOrganizationDetails: updatedOrganizationDetails,
        });
      }
    } catch (error) {}
  };

  renderEditOrganizationFlow = () => {
    const { t, closeModal } = this.props;

    const currentStepClasses = this.getCurrentStepClasses();

    return (
      <div className={classes.container}>
        <div className={classes.scrollContainer} ref={this.scrollContainerRef}>
          <div className={currentStepClasses}>
            {this.getCurrentStepElement()}
          </div>
        </div>
        <div className={classes.footer}>
          <div className={classes.footerContent}>
            <Button variant={"plain"} size={"large"} onClick={closeModal}>
              {t("common:cancel")}
            </Button>
            <Button
              variant={"primary"}
              size={"large"}
              className={classes.nextButton}
              onClick={this.updateOrganizationDetailsHandler}
            >
              {t("common:update")}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  render() {
    const { mode } = this.props;
    return mode === "create"
      ? this.renderCreateOrganizationFlow()
      : this.renderEditOrganizationFlow();
  }
}

const mapActionCreators = { setToastMsg };
const mapStateToProps = (state, ownProps) => {
  const { mode, organizationId } = ownProps;
  const organizationDetails = getOrganizationDetailsByMode({
    mode,
    organizationId,
  });

  const isMyPAMRoute = _.includes(getCurrentPathname(), "accessManagerV2");

  const isManageProgrammesRoute = _.includes(
    getCurrentPathname(),
    "managePrograms"
  );

  const organizationDetailQueryVariables = {
    id: organizationId,
    curriculumProgramFilters: {
      programType: "ALL",
    },
  };
  return {
    mode,
    organizationDetails,
    organizationDetailQueryVariables,
    isManageProgrammesRoute,
    isMyPAMRoute,
    isLoading: false,
    isData: true,
  };
};

export default compose(
  I18nHOC({ resource: ["common"] }),
  connect(mapStateToProps, mapActionCreators),
  graphql(getOrganizationGradesQuery, {
    name: "getOrganizationGrades",
    skip: ({ mode }) => _.includes(["create", "editBasicDetails"], mode),
    options: ({ organizationId }) => ({
      fetchPolicy: "cache-and-network",
      variables: { id: organizationId },
    }),
    props: ({ getOrganizationGrades, ownProps: { isData, isLoading } }) => {
      const grades = _.get(getOrganizationGrades, "node.grades", []);

      const globalGradesGroupedByCurriculumProgram = getGlobalGradesGroupedByCurriculumProgram(
        { grades }
      );

      return {
        globalGradesGroupedByCurriculumProgram,
        isData: !_.isEmpty(getOrganizationGrades) && isData,
        isLoading:
          _.includes([1, 2], getOrganizationGrades["networkStatus"]) ||
          isLoading,
      };
    },
  }),
  graphql(getOrganizationAttachmentDetailsQuery, {
    name: "getOrganizationAttachmentDetails",
    skip: ({ mode }) => _.includes(["create", "editCurriculumPrograms"], mode),
    options: ({ organizationDetailQueryVariables }) => ({
      fetchPolicy: "cache-and-network",
      variables: organizationDetailQueryVariables,
    }),
    props: ({
      getOrganizationAttachmentDetails: { networkStatus },
      ownProps: {
        organizationDetailQueryVariables,
        organizationId,
        isData,
        isLoading,
      },
    }) => {
      const organizationFeedDetails = getOrganizationFeedDetailsFromCache({
        organizationId,
      });
      const organizationAttachmentDetails = getOrganizationAttachmentDetailsFromCache(
        organizationDetailQueryVariables
      );
      const organizationDetails = _.merge(
        organizationFeedDetails,
        organizationAttachmentDetails
      );
      return {
        organizationDetails,
        isData: !_.isEmpty(organizationAttachmentDetails) && isData,
        isLoading:
          _.includes(GRAPHQL_ONGOING_QUERY_NETWORK_STATUS, networkStatus) ||
          isLoading,
      };
    },
  }),
  graphql(getOrganizationDetailsQuery, {
    name: "getOrganizationDetails",
    skip: ({ mode }) => _.includes(["create", "editBasicDetails"], mode),
    options: ({ organizationDetailQueryVariables }) => ({
      fetchPolicy: "cache-and-network",
      variables: organizationDetailQueryVariables,
    }),
    props: ({
      getOrganizationDetails: { networkStatus },
      ownProps: {
        organizationId,
        organizationDetailQueryVariables,
        isData,
        isLoading,
        isManageProgrammesRoute,
        isMyPAMRoute,
      },
    }) => {
      const organizationFeedDetails =
        isManageProgrammesRoute || isMyPAMRoute
          ? getOrganizationFeedDetailsFromCacheLocal(
              organizationDetailQueryVariables
            )
          : getOrganizationFeedDetailsFromCache({
              organizationId,
            });
      const organizationAdditionalDetails = getOrganizationDetailsFromCache(
        organizationDetailQueryVariables
      );
      const organizationDetails = getOrganizationDetails({
        organizationFeedDetails,
        organizationAdditionalDetails,
      });
      return {
        organizationDetails,
        isData: !_.isEmpty(organizationAdditionalDetails) && isData,
        isLoading:
          _.includes(GRAPHQL_ONGOING_QUERY_NETWORK_STATUS, networkStatus) ||
          isLoading,
      };
    },
  }),
  withLoader
)(EditOrgInfo);

EditOrgInfo.defaultProps = {
  mode: "create",
  globalGradesGroupedByCurriculumProgram: {},
};

EditOrgInfo.propTypes = {
  mode: PropTypes.string,
  organizationDetails: PropTypes.object,
  globalGradesGroupedByCurriculumProgram: PropTypes.object,
};
